// Copyright (c) 2017+,COMUNICARE SOLUTIONS SA
// All rights reserved.
//
// Redistribution and use in source and binary forms, with or without modification,
// are NOT permitted.

import { FHIRHelper } from "../helpers/FHIR-helper";
import { EnableWhenBehavior, IEnableWhen } from "../helpers/enableWhen-helper";
import { Tools } from "../helpers/tools-helper";
import { Coding, IEntity, Identifier, Reference, SPECIFIC_USE } from "./sharedInterfaces";

export interface IValueSet {
  id: string;
  values: IKeyValue[];
}

/**
 * Simple KeyValue interface
 */
export interface IKeyValue {
  key: any;
  value: any;
}

export interface Include {
  system: string;
  concept: Coding[];
}

export interface Compose {
  include: Include[];
}

export interface Contained {
  resourceType: string;
  idSet: string;
  name: string;
  status: string;
  description: string;
  compose: Compose;
}

export interface ImageUrls {
  path: string;
  caption: string;
}

export interface Question {
  linkId: string;
  concept: Coding[];
  text: string;
  imageUrls?: ImageUrls[];
  type: string;
  required: boolean;
  repeats: boolean;
  options: Reference;
  option: Coding[];
  answerDisplay?: IAnswerDisplay;
  enableWhen?: IEnableWhen[];
  enableWhenBehavior?: EnableWhenBehavior; // ET | OU
}

export interface Group {
  linkId: string;
  title: string;
  concept: Coding[];
  text: string;
  required: boolean;
  repeats: boolean;
  question?: Question[];
  group?: Group[];
}

export enum INPUT_TYPE {
  RANGE = "range",
  CHECKBOXES = "checkboxes",
  RADIO = "radio",
  DROPDOWN = "dropdown",
  STRING = "string",
  TEXT = "text",
  TEXTFIELD = "textfield",
  NUMBER = "number",
  DECIMAL = "decimal",
  DATE = "date",
  TIME = "time",
  RATING = "rating",
  CHOICE = "choice",
  EXTERNAL_LINK = "externalLink",
}

/**
 * Paging mode
 */
export enum PAGING_MODE {
  NONE = 0, // no paging
  SINGLE = 1, // 1 page by unit
  GROUP = 2, // 1 page for x units
}

export enum QuestionnaireStatus {
  DRAFT = "draft",
  ACTIVE = "active",
  PUBLISHED = "published", // active is the correct status, this is here only because we have some with "published" in DB
  RETIRED = "retired",
}

/**
 * Interface for FHIR Questionnaire
 */
export interface IQuestionnaire extends IEntity {
  resourceType: string;
  contained: Contained[];
  identifier: Identifier[]; // primary key
  version: string; // language version (fr,en,nl,de)
  status: string;
  date: string;
  publisher: string;
  telecom: Identifier[];
  subjectType: string;
  paging: PAGING_MODE;
  group: Group;
  specificUse?: SPECIFIC_USE;
  successThreshold?: number;
  knowledgeId?: string;
  mediaId?: string;
  onlyOnline?: boolean;
  manualTransition?: boolean; // indicates whether you want manual or automatic transitions for a quiz (if not present, automatic)
}

/**
 * IAnswerDisplay
 */
export interface IAnswerDisplay {
  type?: string;
  showMethod?: number;
  min?: string;
  max?: string;
  step?: number;
  default?: string;
  displayIsShown?: boolean;
  placeholder?: string;
  goodAnswers?: string[]; // id list of correct answers to the associated question
  textPass?: string; // text to display when the answer is correct
  textFailed?: string; // text to display when the answer is wrong
}

/**
 * Helper class for IQuestionnaire
 */
export class Questionnaire {
  /**
   * Return identifier for a reponse to a Questionnaire (Caremate Identifier + version)
   * @param q
   */
  public static getIdentifier4Response(q: IQuestionnaire): string {
    for (const ident of q.identifier) {
      if (ident.system === FHIRHelper.SYSTEM_CAREMATE || ident.system === "caremate") return ident.value + "/" + q.version;
    }
    return null;
  }

  public static getIdentifier(q: IQuestionnaire): string {
    for (const ident of q.identifier) {
      if (ident.system === FHIRHelper.SYSTEM_CAREMATE || ident.system === "caremate") return ident.value;
    }
    return null;
  }

  /**
   * check if the arg is of type "IEnableWhen"
   * @param enableWhen
   */
  public static isEnableWhen(enableWhen: any): enableWhen is IEnableWhen {
    return enableWhen.operator && Tools.isDefined(enableWhen.question) && enableWhen.type && Tools.isDefined(enableWhen.answer);
  }
}
